import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type UseVoiceSearchParams = {
  /** Called with the recognized transcript once speech recognition finishes. */
  onResult: (transcript: string) => void;
  /** BCP-47 language override. Defaults to the active i18n language. */
  lang?: string;
};

type UseVoiceSearch = {
  isListening: boolean;
  isSupported: boolean;
  startListening: () => void;
  stopListening: () => void;
};

// Minimal Web Speech API typings — these aren't in TS's DOM lib (and `webkitSpeechRecognition`
// isn't typed at all), so we declare just the surface we use.
type SpeechRecognitionResultEvent = {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
};

type SpeechRecognitionInstance = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionResultEvent) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

// Maps the app's short i18n codes to the BCP-47 tags the Web Speech API expects.
const LANG_MAP: Record<string, string> = {
  en: 'en-US',
  tr: 'tr-TR',
  fr: 'fr-FR',
  de: 'de-DE',
  it: 'it-IT',
  pt: 'pt-PT',
  es: 'es-ES',
  'es-ar': 'es-AR',
  cn: 'zh-CN',
  kr: 'ko-KR',
  'ar-lb': 'ar-LB',
};

const getRecognitionLang = (code?: string): string => {
  if (!code) return 'en-US';
  return LANG_MAP[code] ?? code;
};

const getSpeechRecognitionCtor = (): SpeechRecognitionCtor | undefined => {
  if (typeof window === 'undefined') return undefined;
  // The global itself is untyped, so this single cast is unavoidable.
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition;
};

// Speech APIs throw when called in the wrong state (e.g. start() while already started).
const runSafely = (action: () => void): void => {
  try {
    action();
  } catch {
    /* noop — recognition was in an incompatible state */
  }
};

// Builds a configured one-shot recognition instance with its event handlers wired up.
const createRecognition = (
  Ctor: SpeechRecognitionCtor,
  onResultRef: MutableRefObject<(transcript: string) => void>,
  setIsListening: (value: boolean) => void
): SpeechRecognitionInstance => {
  const recognition = new Ctor();
  recognition.continuous = false;
  recognition.interimResults = false;
  recognition.maxAlternatives = 1;

  recognition.onresult = (event: SpeechRecognitionResultEvent): void => {
    const transcript = (event?.results?.[0]?.[0]?.transcript ?? '').trim();
    if (transcript) onResultRef.current(transcript);
  };
  recognition.onend = (): void => setIsListening(false);
  recognition.onerror = (): void => setIsListening(false);

  return recognition;
};

// Detaches handlers and aborts so a stale instance can't fire after unmount.
const teardownRecognition = (
  recognition: SpeechRecognitionInstance,
  ref: MutableRefObject<SpeechRecognitionInstance | null>
): void => {
  recognition.onresult = null;
  recognition.onend = null;
  recognition.onerror = null;
  runSafely(() => recognition.abort());
  ref.current = null;
};

/**
 * Wraps the browser's Web Speech API for one-shot voice input.
 * Returns `isSupported: false` on browsers without SpeechRecognition (e.g. Firefox),
 * letting callers hide the affordance entirely.
 */
export const useVoiceSearch = ({ onResult, lang }: UseVoiceSearchParams): UseVoiceSearch => {
  const { i18n } = useTranslation();

  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const onResultRef = useRef(onResult);
  const [isListening, setIsListening] = useState<boolean>(false);

  const SpeechRecognitionCtor = getSpeechRecognitionCtor();
  const isSupported = Boolean(SpeechRecognitionCtor);

  // Keep the latest callback without re-creating the recognition instance.
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    // Guard on the ctor itself (not `isSupported`) so TS narrows away `undefined`.
    if (!SpeechRecognitionCtor) return;

    const recognition = createRecognition(SpeechRecognitionCtor, onResultRef, setIsListening);
    recognitionRef.current = recognition;

    return () => teardownRecognition(recognition, recognitionRef);
  }, [isSupported]);

  const startListening = useCallback((): void => {
    const recognition = recognitionRef.current;
    if (!recognition || isListening) return;

    recognition.lang = getRecognitionLang(lang || i18n.language);
    runSafely(() => {
      recognition.start();
      setIsListening(true);
    });
  }, [isListening, lang, i18n.language]);

  const stopListening = useCallback((): void => {
    const recognition = recognitionRef.current;
    if (!recognition) return;

    runSafely(() => recognition.stop());
    setIsListening(false);
  }, []);

  return { isListening, isSupported, startListening, stopListening };
};
